import React, { useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import { FaRobot, FaPlus, FaSyncAlt, FaCheckCircle, FaExclamationTriangle, FaUserPlus, FaCookieBite } from 'react-icons/fa';
import {
  TechSection, TechInput, TechButton, TechButtonGhost, TechSelect,
} from '../../styles/techComponents';
import { FormRow, Label, Hint, BatchResults } from './CreadorPerfilesStyles';
import styled from 'styled-components';
import { batchCreateProfiles, createAndExtractProfile } from '../../services/digitalProfilesService';
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--panel-bg);
  border: 1px solid var(--border-soft);
  border-radius: 8px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CardTitle = styled.h4`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-primary);
`;

const CardDesc = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

const StatusBox = styled.div`
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.85rem;
  ${props => props.$ok ? `
    background: rgba(56, 211, 159, 0.1);
    border: 1px solid var(--alert-success);
    color: var(--alert-success);
  ` : props.$error ? `
    background: rgba(255, 84, 112, 0.1);
    border: 1px solid var(--alert-error);
    color: var(--alert-error);
  ` : `
    background: var(--header-bg);
    border: 1px solid var(--border-soft);
    color: var(--text-secondary);
  `}
`;

const BloqueCuentasReales = ({
  autoRegBusy,
  autoRegResult,
  autoRegSteps,
  autoRegStepStatus,
  handleAutoRegister,
}) => {
  const [autoCount, setAutoCount] = useState(1);
  const [manualAccounts, setManualAccounts] = useState([{ platform: 'x', username: '', login_user: '', email_address: '', password: '', display_name: '' }]);
  const [manualBusy, setManualBusy] = useState(false);
  const [manualResult, setManualResult] = useState(null);

  const addManualAccount = () => {
    if (manualAccounts.length < 5) {
      setManualAccounts([...manualAccounts, { platform: 'x', username: '', login_user: '', email_address: '', password: '', display_name: '' }]);
    }
  };

  const removeManualAccount = (idx) => {
    setManualAccounts(manualAccounts.filter((_, i) => i !== idx));
  };

  const updateManualAccount = (idx, field, value) => {
    const updated = [...manualAccounts];
    updated[idx][field] = value;
    setManualAccounts(updated);
  };

  const handleManualSubmit = async (mode = 'create') => {
    const valid = manualAccounts.every(acc => acc.username.trim() && acc.password.trim());
    if (!valid) {
      alert('Todos los campos de username y password son obligatorios');
      return;
    }
    setManualBusy(true);
    setManualResult(null);
    try {
      const accounts = manualAccounts.map(acc => ({
        platform: acc.platform,
        username: acc.username.trim(),
        login_user: acc.login_user.trim() || acc.username.trim(),
        email_address: acc.email_address.trim() || null,
        password: acc.password,
        display_name: acc.display_name.trim() || acc.username.trim(),
      }));

      let result;
      if (mode === 'extract') {
        // Crear + extraer cookies para cada cuenta (secuencial, puede tardar)
        const results = [];
        for (const acc of accounts) {
          const r = await createAndExtractProfile({ ...acc, auto_extract: true });
          results.push(r);
        }
        const okCount = results.filter(r => r.extract?.body?.ok || r.extract?.status === 200).length;
        result = {
          ok: true,
          created: results.length,
          extracted: okCount,
          details: results,
        };
      } else {
        result = await batchCreateProfiles(accounts);
      }

      setManualResult({ ok: true, mode, ...result });
      if (result.created > 0 || result.extracted > 0) {
        setManualAccounts([{ platform: 'x', username: '', login_user: '', email_address: '', password: '', display_name: '' }]);
      }
    } catch (err) {
      setManualResult({ ok: false, error: friendlyError(err) });
    } finally {
      setManualBusy(false);
    }
  };

  return (
    <TechSection title="🎯 Crear Cuentas Reales" style={{ marginBottom: '1.5rem' }}>
      <p style={{ color: 'var(--text-secondary)', margin: '0 0 1rem 0', fontSize: '0.9rem' }}>
        Crea cuentas con credenciales válidas que pueden iniciar sesión realmente en las plataformas.
      </p>
      
      <TwoCol>
        {/* Auto-registro */}
        <Card>
          <CardTitle><FaRobot /> Auto-Registro X</CardTitle>
          <CardDesc>
            Crea cuentas automáticamente en X/Twitter usando emails temporales. 
            El proceso toma ~2 minutos por cuenta y extrae las cookies automáticamente.
          </CardDesc>
          
          <FormRow style={{ marginTop: '0.5rem' }}>
            <Label>Cantidad (máx 5)</Label>
            <TechInput
              type="number"
              min="1"
              max="5"
              value={autoCount}
              onChange={(e) => setAutoCount(Math.min(Math.max(parseInt(e.target.value) || 1, 1), 5))}
            />
            <Hint>1-5 cuentas por corrida</Hint>
          </FormRow>

          <TechButton
            type="button"
            onClick={() => handleAutoRegister(autoCount)}
            disabled={autoRegBusy}
            style={{ marginTop: '0.5rem' }}
          >
            {autoRegBusy ? (
              <><FaSyncAlt /> Registrando...</>
            ) : (
              <><FaRobot /> Crear {autoCount} cuenta{autoCount > 1 ? 's' : ''} real{autoCount > 1 ? 'es' : ''}</>
            )}
          </TechButton>

          {autoRegResult && (
            <StatusBox $ok={autoRegResult.ok} $error={!autoRegResult.ok} style={{ marginTop: '0.5rem' }}>
              {autoRegResult.ok ? (
                <><FaCheckCircle /> Creadas: {autoRegResult.created} | Fallidas: {autoRegResult.failed}</>
              ) : (
                <><FaExclamationTriangle /> {autoRegResult.error || 'Error en registro'}</>
              )}
            </StatusBox>
          )}
        </Card>

        {/* Creación manual múltiple */}
        <Card>
          <CardTitle><FaUserPlus /> Creación Manual</CardTitle>
          <CardDesc>
            Agrega credenciales reales de cuentas que ya tienes. Útil para importar cuentas existentes.
          </CardDesc>

          <div style={{ maxHeight: '250px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {manualAccounts.map((acc, idx) => (
              <div key={idx} style={{ 
                padding: '0.75rem', 
                background: 'var(--header-bg)', 
                borderRadius: '6px',
                border: '1px solid var(--border-soft)'
              }}>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <TechSelect
                    value={acc.platform}
                    onChange={(e) => updateManualAccount(idx, 'platform', e.target.value)}
                    style={{ width: 'auto' }}
                  >
                    <option value="x">X</option>
                    <option value="instagram">Instagram</option>
                    <option value="facebook">Facebook</option>
                    <option value="linkedin">LinkedIn</option>
                  </TechSelect>
                  {manualAccounts.length > 1 && (
                    <TechButtonGhost 
                      type="button" 
                      onClick={() => removeManualAccount(idx)}
                      style={{ padding: '0.3rem 0.5rem', fontSize: '0.75rem' }}
                    >
                      ✕
                    </TechButtonGhost>
                  )}
                </div>
                <div style={{ display: 'grid', gap: '0.5rem' }}>
                  <TechInput
                    placeholder="Username (@usuario)"
                    value={acc.username}
                    onChange={(e) => updateManualAccount(idx, 'username', e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                  <TechInput
                    placeholder="Login / Usuario (opcional)"
                    value={acc.login_user}
                    onChange={(e) => updateManualAccount(idx, 'login_user', e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                  <TechInput
                    type="email"
                    placeholder="Email (opcional)"
                    value={acc.email_address}
                    onChange={(e) => updateManualAccount(idx, 'email_address', e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                  <TechInput
                    type="password"
                    placeholder="Password"
                    value={acc.password}
                    onChange={(e) => updateManualAccount(idx, 'password', e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                  <TechInput
                    placeholder="Nombre visible (opcional)"
                    value={acc.display_name}
                    onChange={(e) => updateManualAccount(idx, 'display_name', e.target.value)}
                    style={{ fontSize: '0.85rem' }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {manualAccounts.length < 5 && (
              <TechButtonGhost type="button" onClick={addManualAccount} style={{ flex: 1 }}>
                <FaPlus /> Agregar otra
              </TechButtonGhost>
            )}
            <TechButton 
              type="button" 
              onClick={() => handleManualSubmit('create')}
              disabled={manualBusy || manualAccounts.some(acc => !acc.username.trim() || !acc.password.trim())}
              style={{ flex: 1 }}
            >
              {manualBusy ? <><FaSyncAlt /> Creando...</> : <><FaPlus /> Crear {manualAccounts.length}</>}
            </TechButton>
            <TechButton
              type="button"
              onClick={() => handleManualSubmit('extract')}
              disabled={manualBusy || manualAccounts.some(acc => !acc.username.trim() || !acc.password.trim())}
              style={{ flex: 1 }}
            >
              {manualBusy ? <><FaSyncAlt /> Procesando...</> : <><FaCookieBite /> Crear + Auto cookies</>}
            </TechButton>
          </div>

          {manualResult && (
            <StatusBox $ok={manualResult.ok} $error={!manualResult.ok} style={{ marginTop: '0.5rem' }}>
              {manualResult.ok ? (
                manualResult.mode === 'extract' ? (
                  <><FaCheckCircle /> Creadas: {manualResult.created || 0} | Cookies extraídas: {manualResult.extracted || 0} | Fallidas: {(manualResult.created || 0) - (manualResult.extracted || 0)}</>
                ) : (
                  <><FaCheckCircle /> Creadas: {manualResult.created || 0} | Actualizadas: {manualResult.updated || 0} | Errores: {manualResult.errors?.length || 0}</>
                )
              ) : (
                <><FaExclamationTriangle /> {manualResult.error || 'Error al crear cuentas'}</>
              )}
            </StatusBox>
          )}
        </Card>
      </TwoCol>

      {/* Info de cuentas reales vs dummy */}
      <div style={{ 
        marginTop: '1rem', 
        padding: '0.75rem', 
        background: 'rgba(59, 130, 246, 0.1)', 
        borderRadius: '6px',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        fontSize: '0.85rem'
      }}>
        <strong>💡 Diferencia entre cuentas:</strong>
        <ul style={{ margin: '0.5rem 0 0 1.2rem', padding: 0 }}>
          <li><strong>Reales (Auto-registro):</strong> Cuentas nuevas creadas en X con email temporal real, cookies válidas extraídas automáticamente.</li>
          <li><strong>Reales (Manual):</strong> Cuentas existentes tuyas con credenciales válidas que pueden loguearse.</li>
          <li><strong>Dummy:</strong> Perfiles de IA para simular usuarios, útiles para pruebas sin necesidad de login real.</li>
        </ul>
      </div>
    </TechSection>
  );
};

export default BloqueCuentasReales;
